import React, { useState, useEffect, useMemo } from 'react';

import _ from 'lodash';

import { Box, Button, Notification, RadioGroup, Select, Spacer, Spinner, StackView, TextInput, Typography } from '@telus-uds/components-web';

import StatusBar from './statusBar';
import Tooltip from '../form/fields/common/Tooltip';

import request from '../utils/request';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

const NewPhoneNumberCheck = () => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    ORDERS: { labelPleaseSelect },
    portabilityTool: {
      headerCheckAvailibility,
      labelCheckAvailibility,
      labelRateCenter,
      labelRateCenterField,
      labelNpaNxx,
      labelProvince,
      noteRateCenter,
      labelNote,
      buttonClear,
      buttonCheck,
      errorMessage,
      successMessageWithDid,
      successMessageWithNoDid,
      toolTipRateCenter,
      toolTipNpaNxx,
      hintNpaNxx
    }
  } = localizedStrings;

  const [stateValues, setStateValues] = useState({
    isLoading: true,
    type: '',
    province: '',
    rateCenter: '',
    npanxx: '',
    rateCenterData: '',
    rateCenterList: []
  });
  const [status, setStatus] = useState({
    isVisible: false,
    type: '',
    text: ''
  });

  useEffect(() => {
    request
      .get(`/rateCenter?country=CA&sortBy=RCLONG&sortOrder=ASC`)
      .then(response => {
        if (response && Array.isArray(response.data)) {
          setStateValues({
            ...stateValues,
            isLoading: false,
            rateCenterData: response.data
          });
        }
      });
  }, []);

  const rateCenterObjectMap = useMemo(
    () =>
      stateValues.rateCenterData &&
      stateValues.rateCenterData.reduce((acc, rc) => {
        const { npanxx, rclong, newDid, province } = rc;
        if (acc[province]) {
          if (!acc[province][rclong])
            acc[province][rclong] = {
              npanxxList: [],
              name: rclong,
              newDidCount: 0
            };
          if (newDid) acc[province][rclong].newDidCount += 1;
          acc[province][rclong].npanxxList.push({ npanxx, newDid });
        } else {
          acc[province] = {};
        }
        return acc;
      }, {}),
    [stateValues.rateCenterData]
  );

  const checkNewNpaNxx = phoneNumber => {
    let result = '';
    Object.keys(rateCenterObjectMap).forEach(province => {
      Object.keys(rateCenterObjectMap[province]).forEach(rateCenter => {
        const rcData = rateCenterObjectMap[province][rateCenter];
        const searchResult = _.find(rcData.npanxxList, {
          npanxx: phoneNumber
        });
        if (searchResult)
          result = {
            ...rcData
          };
      });
    });
    return result;
  };

  const handleProvinceChange = valueProvince => {
    const list =
      valueProvince &&
      rateCenterObjectMap[valueProvince] &&
      Object.keys(rateCenterObjectMap[valueProvince]).map(
        rc =>
          rateCenterObjectMap[valueProvince][rc].newDidCount > 0 && {
            text: rateCenterObjectMap[valueProvince][rc].name,
            value: rc
          }
      );
    setStateValues({
      ...stateValues,
      province: valueProvince,
      rateCenterList: (list || []).filter(item => item)
    });
  };

  const handleClearFields = () => {
    setStateValues({
      ...stateValues,
      province: '',
      rateCenter: '',
      npanxx: ''
    });
    setStatus({
      ...status,
      isVisible: false
    });
  };

  const handleCheck = () => {
    const searchResult = checkNewNpaNxx(stateValues.npanxx);
    if (searchResult) {
      if (searchResult.newDidCount > 0)
        setStatus({
          isVisible: true,
          type: 'success',
          text: `${successMessageWithDid} ${searchResult.name}`
        });
      else
        setStatus({
          isVisible: true,
          type: 'success',
          text: successMessageWithNoDid
        });
    } else
      setStatus({
        isVisible: true,
        type: 'error',
        text: errorMessage
      });
  };

  const handleSwitch = type => {
    setStateValues({
      ...stateValues,
      type,
      province: '',
      rateCenter: '',
      npanxx: ''
    });
    setStatus({
      ...status,
      isVisible: false
    });
  };

  return (
    <>
      {stateValues.isLoading ? (
        <Spinner show label="Loading ratecenter data..." />
      ) : (
        <StackView space={3}>
          <Typography heading="h2" variant={{ size: 'h2' }} block>
            {headerCheckAvailibility}
          </Typography>
          <StackView space={2} tag="fieldset" style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend>
              <Typography variant={{ bold: true }}>
                {labelCheckAvailibility}
              </Typography>
            </legend>
            <Spacer space={2} />
            <RadioGroup
              legend=""
              items={[
                { 
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {labelRateCenter}
                      <Tooltip copy="en">{toolTipRateCenter}</Tooltip>
                    </div>
                  ), 
                  id: 'RC' 
                },
                { 
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {labelNpaNxx}
                      <Tooltip copy="en">{toolTipNpaNxx}</Tooltip>
                    </div>
                  ), 
                  id: 'NPANXX' 
                }
              ]}
              checkedId={stateValues.type || null}
              onChange={(value) => handleSwitch(value)}
              name="availibility-check"
            />
            <Spacer space={2} />
          </StackView>
          {stateValues.type && (
            <StackView space={2} tag="fieldset" style={{ border: 'none', padding: 0, margin: 0 }}>
              <div
                style={{
                  display: 'flex',
                  marginTop: '1rem'
                }}
              >
                {stateValues.type === 'RC' && (
                  <>
                    <div style={{ width: '20%' }}>
                      <Select
                        label={labelProvince}
                        placeholder={labelPleaseSelect}
                        value={stateValues.province}
                        onChange={handleProvinceChange}
                      >
                        {stateValues.type && [
                          { text: 'Alberta', value: 'AB' },
                          { text: 'British Columbia', value: 'BC' },
                          { text: 'Manitoba', value: 'MB' },
                          { text: 'New Brunswick', value: 'NB' },
                          { text: 'Newfoundland and Labrador', value: 'NL' },
                          { text: 'Nova Scotia', value: 'NS' },
                          { text: 'Ontario', value: 'ON' },
                          { text: 'Prince Edward Island', value: 'PE' },
                          { text: 'Quebec', value: 'QC' },
                          { text: 'Saskatchewan', value: 'SK' }
                        ].map(option => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.text}
                          </Select.Item>
                        ))}
                      </Select>
                    </div>
                    {stateValues.province && (
                      <div style={{ width: '40%', marginLeft: '1rem' }}>
                        <Select
                          label={labelRateCenterField}
                          placeholder={labelPleaseSelect}
                          value={stateValues.rateCenter}
                          onChange={(value) => {
                            setStateValues({
                              ...stateValues,
                              rateCenter: value
                            });
                          }}
                        >
                          {stateValues.rateCenterList && stateValues.rateCenterList.map(option => (
                            <Select.Item key={option.value} value={option.value}>
                              {option.text}
                            </Select.Item>
                          ))}
                        </Select>
                      </div>
                    )}
                  </>
                )}
                {stateValues.type === 'NPANXX' && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ width: '100%' }}>
                      <TextInput
                        type="tel"
                        label={labelNpaNxx}
                        pattern="[0-9]{6}"
                        maxLength="6"
                        value={stateValues.npanxx}
                        onChange={(value) => {
                          setStateValues({
                            ...stateValues,
                            npanxx: value
                          });
                        }}
                        tooltip={hintNpaNxx}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                      <Button variant = {{ priority: 'high' }} onPress={handleCheck}>{buttonCheck}</Button>
                      <Button onPress={handleClearFields}>
                        {buttonClear}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div
                style={{
                  marginTop: '1rem'
                }}
              >
                {stateValues.type === 'RC' && (
                  <Notification variant={{ style: 'subtle' }}>
                    <Typography>
                      <strong>{labelNote}:</strong> {noteRateCenter}
                    </Typography>
                  </Notification>
                )}
                {status.isVisible && (
                  <StatusBar status={status} setStatus={setStatus} />
                )}
              </div>
            </StackView>
          )}
        </StackView>
      )}
    </>
  );
};

export default NewPhoneNumberCheck;
